/**
 * Super-admin School ↔ Linear-customer link (U7).
 *
 * Internal, super-admin-only mapping endpoints that stamp a `School.publicId`
 * onto a Linear customer's `externalIds` (the link the board read path resolves
 * by — KTD2). Cross-school by nature, so the action is keyed off the request
 * body's `school_id` (a School.publicId), NOT the active `x-school-id`.
 *
 *  - GET  /v1/feedback-board/linear-customers?q=<search>  → searchable customer list
 *  - POST /v1/feedback-board/link                         → append-preserve-dedupe link
 *  - DELETE /v1/feedback-board/link                       → idempotent unlink
 *
 * The link write is append-preserve-dedupe: the publicId is added to the
 * customer's EXISTING `externalIds` (e.g. the Linear slug) and de-duped — never
 * a replace. The response echoes the resulting `externalIds` so callers see the
 * merged array.
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-18-002-feat-customer-feedback-roadmap-plan.md (U7)
 */

import { z } from 'zod'

// ============================================================================
// GET /v1/feedback-board/linear-customers?q=<search>
// ============================================================================

/**
 * The school a customer is currently linked to (via one of its `externalIds`
 * matching a `School.publicId`), or null when unlinked. `id` is the
 * School.publicId (response-convention: public ids, never internal).
 */
export const LinkedSchoolSchema = z.object({
  /** School.publicId of the linked school. */
  id: z.string(),
  /** Display name of the linked school. */
  name: z.string()
})
export type LinkedSchool = z.infer<typeof LinkedSchoolSchema>

/** A Linear customer option for the super-admin link picker. */
export const LinearCustomerOptionSchema = z.object({
  /** Linear customer id (opaque; used as `linear_customer_id` in the link request). */
  id: z.string(),
  /** Customer display name (may be absent in Linear). */
  name: z.string().nullable(),
  /** The customer's current `externalIds` (may include the Linear slug + a School.publicId). */
  externalIds: z.array(z.string()),
  /** The School this customer is already linked to, or null when none of its externalIds match a School. */
  linkedSchool: LinkedSchoolSchema.nullable()
})
export type LinearCustomerOption = z.infer<typeof LinearCustomerOptionSchema>

/** Response for the customer search/list endpoint. */
export const FeedbackBoardLinearCustomersResponseSchema = z.object({
  /** The matched customers (filtered by `q`, capped). */
  customers: z.array(LinearCustomerOptionSchema)
})
export type FeedbackBoardLinearCustomersResponse = z.infer<typeof FeedbackBoardLinearCustomersResponseSchema>

// ============================================================================
// POST /v1/feedback-board/link
// ============================================================================

/** Link request — name the school (by publicId) and the Linear customer to link it to. */
export const LinkLinearCustomerRequestSchema = z.object({
  /** School.publicId to stamp onto the customer's externalIds. */
  school_id: z.string().trim().min(1),
  /** Linear customer id to link the school to. */
  linear_customer_id: z.string().trim().min(1)
})
export type LinkLinearCustomerRequest = z.infer<typeof LinkLinearCustomerRequestSchema>

/** The resulting customer state after a link/unlink (echoes the merged externalIds). */
export const LinkedCustomerSchema = z.object({
  /** Linear customer id. */
  id: z.string(),
  /** The customer's externalIds after the write (the append-preserve-dedupe result). */
  externalIds: z.array(z.string())
})
export type LinkedCustomer = z.infer<typeof LinkedCustomerSchema>

/**
 * Link response. `already_linked` is true when the school_id was already present
 * on the customer (idempotent no-op — no write performed); the `customer` echoes
 * the resulting externalIds either way.
 */
export const LinkLinearCustomerResponseSchema = z.object({
  /** Always true on success — the school is linked to the customer. */
  linked: z.literal(true),
  /** True when the link already existed (no write performed); false when a write stamped it. */
  already_linked: z.boolean(),
  /** The customer with its resulting externalIds. */
  customer: LinkedCustomerSchema
})
export type LinkLinearCustomerResponse = z.infer<typeof LinkLinearCustomerResponseSchema>

// ============================================================================
// DELETE /v1/feedback-board/link
// ============================================================================

/** Unlink request — reuses the link request shape (school_id + linear_customer_id). */
export const UnlinkLinearCustomerRequestSchema = LinkLinearCustomerRequestSchema
export type UnlinkLinearCustomerRequest = z.infer<typeof UnlinkLinearCustomerRequestSchema>

/**
 * Unlink response. `unlinked` is always true on success; idempotent (removing an
 * absent school_id is a no-op success). `customer` echoes the remaining externalIds.
 */
export const UnlinkLinearCustomerResponseSchema = z.object({
  /** Always true on success — the school is not linked to the customer. */
  unlinked: z.literal(true),
  /** The customer with its remaining externalIds (school_id removed). */
  customer: LinkedCustomerSchema
})
export type UnlinkLinearCustomerResponse = z.infer<typeof UnlinkLinearCustomerResponseSchema>

// ============================================================================
// GET /v1/feedback-board/schools/:school_id/linear-customer
//   → the customer currently linked to this school (resolved by externalId), or
//     null when none. Powers the admin "Linear Issues" tab's on-load check.
// POST /v1/feedback-board/schools/:school_id/linear-customer
//   → create a Linear customer for the school and stamp its externalId in one
//     call (create-and-link). Use when no customer exists yet.
// ============================================================================

/** The Linear customer linked to a school (summary for the admin tab). */
export const SchoolLinearCustomerSchema = z.object({
  /** Linear customer id. */
  id: z.string(),
  /** Customer display name (may be absent in Linear). */
  name: z.string().nullable(),
  /** Approximate number of customer needs (board cards) on this customer. */
  need_count: z.number().int().min(0)
})
export type SchoolLinearCustomer = z.infer<typeof SchoolLinearCustomerSchema>

/**
 * An existing Linear customer that matches this school BY NAME but is NOT yet
 * stamped with its publicId — surfaced so the admin can one-click link it
 * instead of searching. Null when no unstamped name match exists.
 */
export const LinearCustomerSuggestionSchema = z.object({
  /** Linear customer id (used as `linear_customer_id` to link). */
  id: z.string(),
  /** Customer display name (the name that matched the school). */
  name: z.string().nullable()
})
export type LinearCustomerSuggestion = z.infer<typeof LinearCustomerSuggestionSchema>

/** Response for the per-school linked-customer lookup. */
export const GetSchoolLinearCustomerResponseSchema = z.object({
  /** True when a Linear customer carries this school's publicId in its externalIds. */
  linked: z.boolean(),
  /** The linked customer summary, or null when the school isn't linked yet. */
  customer: SchoolLinearCustomerSchema.nullable(),
  /**
   * When NOT linked: an existing unstamped Linear customer whose name matches the
   * school (one-click link candidate), or null when none — in which case the
   * school has no Linear company and should be created.
   */
  suggestion: LinearCustomerSuggestionSchema.nullable()
})
export type GetSchoolLinearCustomerResponse = z.infer<typeof GetSchoolLinearCustomerResponseSchema>

/** Create-customer request body — the display name for the new Linear customer. */
export const CreateSchoolLinearCustomerRequestSchema = z.object({
  /** Display name for the new Linear customer (defaults to the school name client-side). */
  name: z.string().trim().min(1)
})
export type CreateSchoolLinearCustomerRequest = z.infer<typeof CreateSchoolLinearCustomerRequestSchema>

/** Create-customer response — the new customer with its resulting externalIds. */
export const CreateSchoolLinearCustomerResponseSchema = z.object({
  /** Always true on success. */
  created: z.literal(true),
  /** The created (or upserted) customer with the school's externalId stamped. */
  customer: LinkedCustomerSchema
})
export type CreateSchoolLinearCustomerResponse = z.infer<typeof CreateSchoolLinearCustomerResponseSchema>
